/**
 * Bella Staging - Animation Core (Page Load Version)
 * UPDATED: Better integration with page loader
 */

(function() {
  'use strict';

  let isPageLoaded = false;
  let animationsInitialized = false;

  // Configuration
  const CONFIG = {
    pageLoadDelay: 0, // No delay when used with page loader
    autoStart: true
  };

  // Wait for GSAP to load
  function initAnimations() {
    // Check if GSAP is available
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.error('GSAP or ScrollTrigger not loaded!');
      return;
    }

    console.log('✅ GSAP Loaded!');

    // Register ScrollTrigger
    gsap.registerPlugin(ScrollTrigger);

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      console.log('⚠️ Reduced motion detected - animations disabled');
      document.querySelectorAll('[data-animate]').forEach(el => {
        el.style.opacity = '1';
      });
      return;
    }

    // Set all animated elements to initial hidden state
    setInitialStates();

    // UPDATED: Better page loader integration
    if (document.getElementById('pageTransition')) {
      // Check if page loader already completed
      if (window.pageLoaderComplete) {
        console.log('⚡ Page loader already complete! Starting animations immediately...');
        startAnimations();
      } else {
        // Wait for page loader to complete
        console.log('⏳ Waiting for page loader...');
        window.addEventListener('pageLoaderComplete', function() {
          console.log('🎬 Page loader complete! Starting animations...');
          startAnimations();
        });
      }
    } else {
      // No page loader, start on page load
      if (document.readyState === 'complete') {
        startAnimations();
      } else {
        window.addEventListener('load', startAnimations);
      }
    }
  }

  /**
   * SET INITIAL STATES
   */
  function setInitialStates() {
    const allAnimatedElements = document.querySelectorAll('[data-animate]');
    
    allAnimatedElements.forEach(element => {
      gsap.set(element, { opacity: 0 });
      
      const animationType = element.dataset.animate;
      const direction = element.dataset.animateDirection || 'bottom';
      const distance = parseFloat(element.dataset.animateDistance) || 30;
      
      switch(animationType) {
        case 'fade-in':
        case 'slide-in':
          if (direction === 'top') gsap.set(element, { y: -distance });
          else if (direction === 'bottom') gsap.set(element, { y: distance });
          else if (direction === 'left') gsap.set(element, { x: -distance });
          else if (direction === 'right') gsap.set(element, { x: distance });
          break;
          
        case 'scale':
          const scaleFrom = parseFloat(element.dataset.animateScale) || 0.8;
          gsap.set(element, { scale: scaleFrom });
          break;
          
        case 'rotate':
          const rotation = parseFloat(element.dataset.animateRotate) || 180;
          const scale = parseFloat(element.dataset.animateScale) || 0.5;
          gsap.set(element, { rotation: rotation, scale: scale });
          break;
          
        case 'clip-reveal':
          const clipDirection = element.dataset.animateDirection || 'right';
          let clipPath;
          switch(clipDirection) {
            case 'left': clipPath = 'inset(0% 100% 0% 0%)'; break;
            case 'right': clipPath = 'inset(0% 0% 0% 100%)'; break;
            case 'top': clipPath = 'inset(100% 0% 0% 0%)'; break;
            case 'bottom': clipPath = 'inset(0% 0% 100% 0%)'; break;
          }
          gsap.set(element, { clipPath: clipPath });
          break;
      }
    });

    console.log(`📦 Initial states set for ${allAnimatedElements.length} elements`);
  }

  /**
   * START ANIMATIONS
   */
  function startAnimations() {
    if (animationsInitialized) return;
    
    isPageLoaded = true;
    console.log('🚀 Starting animations...');

    // Add global delay before starting animations
    setTimeout(() => {
      initFadeIn();
      initSlideIn();
      initScale();
      initStagger();
      initClipReveal();
      initRotate();

      animationsInitialized = true;

      // Refresh ScrollTrigger
      setTimeout(() => {
        ScrollTrigger.refresh();
        console.log('🔄 ScrollTrigger refreshed');
      }, 100);

      console.log('✅ All animations started!');
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('bella:animations:complete'));
    }, CONFIG.pageLoadDelay * 1000);
  }

  /**
   * FADE IN ANIMATION
   */
  function initFadeIn() {
    const elements = document.querySelectorAll('[data-animate="fade-in"]');
    
    elements.forEach((element) => {
      const delay = parseFloat(element.dataset.animateDelay) || 0;
      const duration = parseFloat(element.dataset.animateDuration) || 1;

      let toVars = {
        opacity: 1,
        x: 0,
        y: 0,
        duration: duration,
        delay: delay,
        ease: 'power2.out'
      };

      gsap.to(element, {
        ...toVars,
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
          onEnter: () => element.classList.add('animated')
        }
      });
    });

    console.log(`✓ Fade-in: ${elements.length} elements`);
  }

  /**
   * SLIDE IN ANIMATION
   */
  function initSlideIn() {
    const elements = document.querySelectorAll('[data-animate="slide-in"]');
    
    elements.forEach((element) => {
      const delay = parseFloat(element.dataset.animateDelay) || 0;
      const duration = parseFloat(element.dataset.animateDuration) || 1;

      gsap.to(element, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: duration,
        delay: delay,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
          onEnter: () => element.classList.add('animated')
        }
      });
    });

    console.log(`✓ Slide-in: ${elements.length} elements`);
  }

  /**
   * SCALE ANIMATION
   */
  function initScale() {
    const elements = document.querySelectorAll('[data-animate="scale"]');
    
    elements.forEach((element) => {
      const delay = parseFloat(element.dataset.animateDelay) || 0;
      const duration = parseFloat(element.dataset.animateDuration) || 1;

      gsap.to(element, {
        scale: 1,
        opacity: 1,
        duration: duration,
        delay: delay,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
          onEnter: () => element.classList.add('animated')
        }
      });
    });

    console.log(`✓ Scale: ${elements.length} elements`);
  }

  /**
   * STAGGER ANIMATION
   */
  function initStagger() {
    const containers = document.querySelectorAll('[data-animate="stagger"]');
    
    containers.forEach((container) => {
      const delay = parseFloat(container.dataset.animateDelay) || 0;
      const duration = parseFloat(container.dataset.animateDuration) || 0.8;
      const stagger = parseFloat(container.dataset.animateStagger) || 0.1;
      const direction = container.dataset.animateDirection || 'bottom';
      const distance = parseFloat(container.dataset.animateDistance) || 30;

      const children = Array.from(container.children);

      // Set initial state for children
      children.forEach(child => {
        gsap.set(child, { opacity: 0 });
        
        if (direction === 'top') gsap.set(child, { y: -distance });
        else if (direction === 'bottom') gsap.set(child, { y: distance });
        else if (direction === 'left') gsap.set(child, { x: -distance });
        else if (direction === 'right') gsap.set(child, { x: distance });
      });

      gsap.to(children, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: duration,
        stagger: stagger,
        ease: 'power2.out',
        delay: delay,
        scrollTrigger: {
          trigger: container,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
          onEnter: () => container.classList.add('animated')
        }
      });
    });

    console.log(`✓ Stagger: ${containers.length} containers`);
  }

  /**
   * CLIP REVEAL ANIMATION
   */
  function initClipReveal() {
    const elements = document.querySelectorAll('[data-animate="clip-reveal"]');
    
    elements.forEach((element) => {
      const delay = parseFloat(element.dataset.animateDelay) || 0;
      const duration = parseFloat(element.dataset.animateDuration) || 1.5;

      gsap.to(element, {
        clipPath: 'inset(0% 0% 0% 0%)',
        duration: duration,
        delay: delay,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
          onEnter: () => element.classList.add('animated')
        }
      });
    });

    console.log(`✓ Clip reveal: ${elements.length} elements`);
  }

  /**
   * ROTATE ANIMATION
   */
  function initRotate() {
    const elements = document.querySelectorAll('[data-animate="rotate"]');
    
    elements.forEach((element) => {
      const delay = parseFloat(element.dataset.animateDelay) || 0;
      const duration = parseFloat(element.dataset.animateDuration) || 1;

      gsap.to(element, {
        rotation: 0,
        scale: 1,
        opacity: 1,
        duration: duration,
        delay: delay,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
          onEnter: () => element.classList.add('animated')
        }
      });
    });

    console.log(`✓ Rotate: ${elements.length} elements`);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnimations);
  } else {
    initAnimations();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', function() {
    console.log('🔄 Section loaded, refreshing animations...');
    animationsInitialized = false;
    setTimeout(() => {
      ScrollTrigger.getAll().forEach(t => t.kill());
      initAnimations();
    }, 100);
  });

  // Expose config for customization
  window.BellaAnimationConfig = CONFIG;

})();